import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;
export const STORAGE_KEY = "realtor_crm_token";

const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      // token rejected — clear it and let UI redirect to login
      if (!window.location.pathname.endsWith("/login")) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    // 429 auto-retry with exponential backoff. Emergent's ingress and
    // Cloudflare occasionally rate-limit bursts (multi-tab polling +
    // rapid search debounces + JobsContext + enrichment progress polls
    // can cluster in a small window). Retry silently up to 3 times so
    // transient throttles never surface as a user-visible error toast.
    // Respects `Retry-After` header when present.
    if (status === 429) {
      const cfg = err.config || {};
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= 3) {
        const retryAfter = Number(err.response.headers?.["retry-after"]);
        // Cap at 4s so the UI doesn't feel frozen — 400ms, 800ms, 1600ms
        // default, or the server's Retry-After if it's shorter.
        const backoffMs = Number.isFinite(retryAfter) && retryAfter > 0
          ? Math.min(retryAfter * 1000, 4000)
          : Math.min(200 * 2 ** cfg.__retryCount, 4000);
        await new Promise((res) => setTimeout(res, backoffMs));
        return api(cfg);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function buildPhotoUrl(realtor) {
  if (!realtor) return "";
  if (realtor.photo_path) {
    // photo_path is /app/backend/static/photos/<file>. Serve via /api/static/photos/<file>
    // because the K8s ingress only forwards /api/* to the FastAPI process; a bare
    // /static/... request would land on the React dev server and return the SPA
    // shell instead of the JPEG. The backend mounts both /static and /api/static
    // at the same directory so downloads/vcards keep working.
    const filename = realtor.photo_path.split("/").pop();
    return `${BASE}/api/static/photos/${filename}`;
  }
  return realtor.photo_url || "";
}

/**
 * Return the highest-resolution URL we know how to derive for a realtor's
 * photo. Used by the avatar lightbox so tapping the tiny thumbnail on
 * mobile enlarges to a full-detail headshot instead of upscaling the
 * 55×72 `lowres/` thumbnail past its native resolution.
 *
 * Mirrors `backend/photo_check.py:_upgrade_url` — realtor.ca has a
 * `/highres/` variant next to the default `/lowres/`, and myrealpage
 * has `/g:md/` next to `/g:sm/`. Any URL we don't recognise is returned
 * unchanged; the lightbox falls back to the original URL if the
 * upgraded variant 404s (via `onError` on the <img>).
 */
export function buildHighResPhotoUrl(realtor) {
  const url = buildPhotoUrl(realtor);
  if (!url) return "";
  if (url.includes("cdn.realtor.ca") && url.includes("/lowres/")) {
    return url.replace("/lowres/", "/highres/");
  }
  if (url.includes("iss-cdn.myrealpage.com")) {
    return url.replace("/g:sm/", "/g:md/");
  }
  return url;
}

export default api;
